import { useMemo } from 'react';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { useHasQualityControlPermission } from '../../QualityControl/useHasQcPermission';
import { useWorklistCustomStatuses, formatStatusName } from 'hooks/useWorklistCustomStatuses';
import { getWorklistStatusFilterItems, mapCriticalResultStatusToNumber } from '../utils';
import { subDays } from 'date-fns/subDays';

import { FilterVariant, WorklistFilterSourceName, WorklistFilters } from '../types';
import type {
  FilterDefinitionType,
  MultiSelectFilterItemType,
  MultiSelectFilterType,
  DateRangeFilterType,
  NumberRangeFilterType,
  TextFilterType,
} from '../types';
import { EXCLUDED_EXAM_CODES } from '../constants';
import { WorklistItemStatus, CriticalResultStatus } from 'generated/graphql';
import { WithApolloContext } from 'modules/Apollo/types';

type WorklistFilterConfigV2 = {
  availableFilters: FilterDefinitionType;
};

export const useGenerateFilterConfigV2 = ({
  context,
  skipQueries = false,
}: WithApolloContext & { skipQueries?: boolean }): WorklistFilterConfigV2 => {
  const [isQcStatusColumnEnabled] = useFeatureFlagEnabled(FF.QC_STATUS_COLUMN);
  const [isReportStatusColumnEnabled] = useFeatureFlagEnabled(FF.WORKLIST_REPORT_STATUS_COLUMN);

  const hasQualityControlPermission = useHasQualityControlPermission({ context });
  const { isLoadingCustomStatuses } = useWorklistCustomStatuses({
    context,
    skipQuery: skipQueries,
  });

  const excludedSites = useMemo(() => /(?:-)?(?:MISMATCHED[ _]STUDY|PRIOR[ _]STUDY)$/i, []);

  const availableFilters: FilterDefinitionType = useMemo(() => {
    const filters: FilterDefinitionType = {
      [WorklistFilters.PatientSex]: {
        label: 'Gender',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        source: WorklistFilterSourceName.PatientSex,
      } as MultiSelectFilterType,
      [WorklistFilters.StudyDate]: {
        label: 'Exam Date',
        type: FilterVariant.DateRange,
        supportsRelativeDates: true,
      } as DateRangeFilterType,
      [WorklistFilters.PatientDob]: {
        label: 'DOB',
        type: FilterVariant.DateRange,
      } as DateRangeFilterType,
      [WorklistFilters.Modality]: {
        label: 'Modality',
        type: FilterVariant.MultiSelect,
        isSearchable: true,
        source: WorklistFilterSourceName.Modality,
      } as MultiSelectFilterType,
      [WorklistFilters.SubmittedAt]: {
        label: 'Submitted At',
        type: FilterVariant.DateRange,
        supportsRelativeDates: true,
        minDate: subDays(new Date(), 30),
      } as DateRangeFilterType,
      [WorklistFilters.CustomStatus]: {
        label: 'Workflow Status',
        type: FilterVariant.MultiSelect,
        isLoading: isLoadingCustomStatuses,
        source: WorklistFilterSourceName.CustomStatus,
        formatItems: (items: MultiSelectFilterItemType[]) =>
          items.map((item) => ({
            label: formatStatusName(item.label),
            value: item.value,
            count: item.count,
          })),
      } as MultiSelectFilterType,
      [WorklistFilters.Views]: {
        label: 'Views',
        type: FilterVariant.MultiSelect,
        hidden: true,
        items: [],
        source: '' as const,
      } as MultiSelectFilterType,
      [WorklistFilters.ExamStatus]: {
        label: 'Status',
        type: FilterVariant.MultiSelect,
        items: getWorklistStatusFilterItems(hasQualityControlPermission),
        forceLockDefaultSelection: true,
      } as MultiSelectFilterType,
      [WorklistFilters.ClaimedBy]: {
        label: 'Claimed By',
        type: FilterVariant.MultiSelect,
        hidden: true,
      } as MultiSelectFilterType,
      [WorklistFilters.ExamCode]: {
        label: 'Exam Code',
        type: FilterVariant.MultiSelect,
        source: WorklistFilterSourceName.ExamCode,
        isSearchable: true,
        formatItems: (items: MultiSelectFilterItemType[]) =>
          items.filter((item) => !EXCLUDED_EXAM_CODES.test(item.label)),
      } as MultiSelectFilterType,
      [WorklistFilters.HasDraftReport]: {
        label: 'Draft',
        type: FilterVariant.MultiSelect,
        items: [
          { value: 'true', label: 'Has Draft' },
          { value: 'false', label: 'No Draft' },
        ],
        isBoolean: true,
        source: WorklistFilterSourceName.HasDraftReport,
        formatItems: (items: MultiSelectFilterItemType[]) =>
          items.map((item) => ({
            value: String(item.value).toLowerCase(),
            label: item.value === 'True' ? 'Has Draft' : 'No Draft',
            count: item.count,
          })),
      } as MultiSelectFilterType,
      [WorklistFilters.Age]: {
        label: 'Age',
        type: FilterVariant.NumberRange,
      } as NumberRangeFilterType,
      [WorklistFilters.AccessionNumber]: {
        label: 'Accession #',
        type: FilterVariant.Text,
      } as TextFilterType,
      [WorklistFilters.Mrn]: {
        label: 'MRN #',
        type: FilterVariant.Text,
      } as TextFilterType,
      [WorklistFilters.PatientType]: {
        label: 'Patient Type',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        source: WorklistFilterSourceName.PatientType,
      } as MultiSelectFilterType,
      [WorklistFilters.StudyDescription]: {
        label: 'Exam Description',
        type: FilterVariant.Text,
      } as TextFilterType,
      [WorklistFilters.Site]: {
        label: 'Site',
        type: FilterVariant.MultiSelect,
        isSearchable: true,
        source: WorklistFilterSourceName.Site,
        formatItems: (items: MultiSelectFilterItemType[]) =>
          items.filter((item) => !excludedSites.test(item.label)),
      } as MultiSelectFilterType,
      [WorklistFilters.BodyPart]: {
        label: 'Body Part',
        type: FilterVariant.MultiSelect,
        isSearchable: true,
        source: WorklistFilterSourceName.BodyPart,
      } as MultiSelectFilterType,
      [WorklistFilters.PatientName]: {
        label: 'Name',
        type: FilterVariant.Text,
        placeholder: 'Search by name',
      } as TextFilterType,
      [WorklistFilters.Priority]: {
        label: 'Priority',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        source: WorklistFilterSourceName.Priority,
      } as MultiSelectFilterType,
      [WorklistFilters.SignedBy]: {
        label: 'Signed By',
        type: FilterVariant.Text,
        placeholder: 'Search by name or email',
      } as TextFilterType,
      [WorklistFilters.Status]: {
        label: 'Status',
        source: WorklistFilterSourceName.Status,
        type: FilterVariant.MultiSelect,
        hidden: true,
      } as MultiSelectFilterType,
      [WorklistFilters.CriticalResult]: {
        label: 'Critical Workflow',
        type: FilterVariant.MultiSelect,
        items: [
          {
            value: mapCriticalResultStatusToNumber(CriticalResultStatus.Critical),
            label: 'Critical',
          },
          {
            value: mapCriticalResultStatusToNumber(CriticalResultStatus.CriticalAcknowledged),
            label: 'Critical Acknowledged',
          },
          {
            value: mapCriticalResultStatusToNumber(CriticalResultStatus.NotCritical),
            label: 'Not Critical',
          },
        ],
        isSearchable: false,
      } as MultiSelectFilterType,
    };

    filters[WorklistFilters.Radiologist] = {
      label: 'Assigned To',
      type: FilterVariant.Text,
      placeholder: 'Search by name or email',
    } as TextFilterType;
    filters[WorklistFilters.ReferringPhysician] = {
      label: 'Ordering Physician',
      type: FilterVariant.Text,
      placeholder: 'Search by name',
    } as TextFilterType;
    filters[WorklistFilters.Specialty] = {
      label: 'Specialty',
      type: FilterVariant.Text,
    } as TextFilterType;

    if (isReportStatusColumnEnabled) {
      filters[WorklistFilters.ReportExtendedStatus] = {
        label: 'Report Status',
        type: FilterVariant.MultiSelect,
        isSearchable: true,
        source: WorklistFilterSourceName.ReportExtendedStatus,
      } as MultiSelectFilterType;
    }

    if (isQcStatusColumnEnabled) {
      filters[WorklistFilters.QcStatus] = {
        label: 'Status',
        type: FilterVariant.MultiSelect,
        isSearchable: false,
        items: [
          { value: WorklistItemStatus.Qc, label: 'QC', defaultSelected: true },
          {
            value: WorklistItemStatus.InTransit,
            label: 'In Transit',
            defaultSelected: true,
          },
          {
            value: WorklistItemStatus.Mismatched,
            label: 'Mismatched',
            defaultSelected: true,
          },
          {
            value: WorklistItemStatus.Scheduled,
            label: 'Scheduled',
            defaultSelected: true,
          },
        ],
      } as MultiSelectFilterType;
    }

    return filters;
  }, [
    excludedSites,
    hasQualityControlPermission,
    isLoadingCustomStatuses,
    isQcStatusColumnEnabled,
    isReportStatusColumnEnabled,
  ]);

  return { availableFilters };
};

export type { WorklistFilterConfigV2 };
